import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FiArrowRight, FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AgrogridBrandLogo from "../../../../../assets/images/agrogrid-brand.png";
import AgrogridLogo from "../../../../../assets/images/agrogrid-logo.png";
import { getAdminRedirectPath, useAdminAuth } from "../../../../../providers/adminAuthentication.provider";
import { Wrapper } from "../login.style";

export const LoginSection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading } = useAdminAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    defaultValues: { username: "", password: "" },
  });

  const onSubmit = async (credentials) => {
    setServerError("");
    const result = await login(credentials);

    if (result.success) {
      const role = result.data.role;
      navigate(getAdminRedirectPath(searchParams.get("redirect_uri"), role), { replace: true });
      return;
    }

    setServerError(result.message || "We couldn't sign you in. Please try again.");
  };

  return (
    <Wrapper>
      <section className="login-panel" aria-labelledby="admin-login-title">
        <Link className="brand" to="/" aria-label="Agrogrid home">
          <img src={AgrogridBrandLogo} alt="Agrogrid" />
        </Link>

        <div className="form-shell">
          <div className="mobile-mark" aria-hidden="true">
            <img src={AgrogridLogo} alt="" />
          </div>
          <span className="eyebrow">Admin portal</span>
          <h1 id="admin-login-title">Welcome back</h1>
          <p className="intro">Sign in to manage Agrogrid operations, orders and Split-Buy resources.</p>

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {serverError && (
              <div className="server-error" role="alert">
                {serverError}
              </div>
            )}

            <div className="field-group">
              <label htmlFor="admin-email">Email address</label>
              <div className={`field ${errors.username ? "has-error" : ""}`}>
                <FiMail aria-hidden="true" />
                <Controller
                  name="username"
                  control={control}
                  rules={{
                    required: "Enter your email address",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Enter a valid email address",
                    },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="admin-email"
                      type="email"
                      autoComplete="username"
                      placeholder="admin@agrogrid.com"
                      aria-invalid={!!errors.username}
                      aria-describedby={errors.username ? "admin-email-error" : undefined}
                    />
                  )}
                />
              </div>
              {errors.username && <p id="admin-email-error" className="field-error">{errors.username.message}</p>}
            </div>

            <div className="field-group">
              <div className="label-row">
                <label htmlFor="admin-password">Password</label>
                  <Link to="/reset-password">Forgot password?</Link>
              </div>
              <div className={`field ${errors.password ? "has-error" : ""}`}>
                <FiLock aria-hidden="true" />
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: "Enter your password" }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="admin-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      aria-invalid={!!errors.password}
                      aria-describedby={errors.password ? "admin-password-error" : undefined}
                    />
                  )}
                />
                <button
                  className="visibility-toggle"
                  type="button"
                  onClick={() => setShowPassword((current) => !current)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.password && <p id="admin-password-error" className="field-error">{errors.password.message}</p>}
            </div>

            <button className="submit-button" type="submit" disabled={isLoading}>
              <span>{isLoading ? "Signing in…" : "Sign in to dashboard"}</span>
              {isLoading ? <span className="spinner" aria-hidden="true" /> : <FiArrowRight aria-hidden="true" />}
            </button>
          </form>

          <p className="help-text">Having trouble signing in? <a href="mailto:support@agrogrid.com">Contact support</a></p>
        </div>

        <p className="copyright">© {new Date().getFullYear()} Agrogrid. All rights reserved.</p>
      </section>

      <aside className="visual-panel" aria-hidden="true">
        <div className="visual-content">
          <div className="logo-mark"><img src={AgrogridLogo} alt="" /></div>
          <p className="quote">Growing better outcomes, one decision at a time.</p>
          <span>Agrogrid Operations</span>
        </div>
      </aside>
    </Wrapper>
  );
};

export default LoginSection;
